import { useState } from 'react';

import { DateRangePicker, Input, Select, Text, type TypeSelectOption } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { SearchFilter } from '~/components/common';

import { getAllTemperatureOptions } from '~/utils';

import { OPTION_ALL } from '~/constants';

import type {
  TypeReceiveFilterOptions,
  TypeReceiveReportFilter,
  TypeReceiveReportFilterStatus,
} from '~/types/receive.types';

import styles from './ReceiveReportFilter.module.scss';

const ReceiveReportFilter = ({
  options,
  values,
  valuesState,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
}: {
  options?: TypeReceiveFilterOptions;
  values: TypeReceiveReportFilter;
  valuesState: TypeReceiveReportFilterStatus;
  onChange: (values: Partial<TypeReceiveReportFilter>) => void;
  onChangeStatus: (values: Partial<TypeReceiveReportFilterStatus>) => void;
  onSearch: () => void;
  onReset: () => void;
}) => {
  /** 입고 센터 선택값에 따른 온도대 필터 옵션 */
  const [optionTemperature, setOptionTemperature] = useState<TypeSelectOption<string>[]>([]);

  return (
    <SearchFilter formId="receive-report-search-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text size="small" weight="semi-bold" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options?.keywordType ?? []}
            value={values.keywordType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
            }}
          />
          <Input
            type="text"
            size="small"
            placeholder="입력"
            value={values.keyword}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-center'])}>
        <Text size="small" weight="semi-bold" element="p">
          입고 센터
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options?.center ?? []}
            value={values.cluster}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.cluster?.value) return;
              onChange({ cluster: value, warehouse: OPTION_ALL });
              setOptionTemperature(
                value.value === OPTION_ALL.value
                  ? getAllTemperatureOptions(options?.groupedTemperatureByCenter ?? {})
                  : (options?.groupedTemperatureByCenter[value.value] ?? [])
              );
            }}
          />
          <Select
            size="small"
            placeholder="선택"
            disabled={values.cluster.value === OPTION_ALL.value}
            options={optionTemperature}
            value={values.warehouse}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.warehouse?.value) return;
              onChange({ warehouse: value });
            }}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <Text size="small" weight="semi-bold" element="p">
          기간
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options?.dateType ?? []}
            value={values.dateType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.dateType?.value) return;
              onChange({ dateType: value });
            }}
          />
          <DateRangePicker
            size="small"
            placeholder={{ start: '시작일', end: '종료일' }}
            status={valuesState.date}
            value={values.date}
            onChange={(value) => {
              onChange({ date: value });
              onChangeStatus({ date: 'default' });
            }}
            onFocus={() => onChangeStatus({ date: 'default' })}
          />
        </div>
      </div>
      <div className={styles['filter-container']}>
        <Text size="small" weight="semi-bold" element="p">
          입고 방법
        </Text>
        <Select
          size="small"
          placeholder="선택"
          options={options?.releaseCode ?? []}
          value={values.releaseCode}
          onChange={(_: unknown, value: TypeSelectOption<string>) => {
            if (value?.value === values.releaseCode?.value) return;
            onChange({ releaseCode: value });
          }}
        />
      </div>
      <div className={styles['filter-container']}>
        <Text size="small" weight="semi-bold" element="p">
          리포트 상태
        </Text>
        <Select
          size="small"
          placeholder="선택"
          options={options?.reportStatus ?? []}
          value={values.reportStatus}
          onChange={(_: unknown, value: TypeSelectOption<string>) => {
            if (value?.value === values.reportStatus?.value) return;
            onChange({ reportStatus: value });
          }}
        />
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-work-type'])}>
        <Text size="small" weight="semi-bold" element="p">
          작업 유형
        </Text>
        <Select
          size="small"
          placeholder="선택"
          options={options?.reportTaskCodes ?? []}
          value={values.workType}
          onChange={(_: unknown, value: TypeSelectOption<string>) => {
            if (value?.value === values.workType?.value) return;
            onChange({ workType: value });
          }}
        />
      </div>
    </SearchFilter>
  );
};

export default ReceiveReportFilter;
